import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { PersonRequest } from '@app/models/project-setup.models';
import { PersonSimpleWithRole } from '@app/models/project.models';
import { PersonRoleType } from '@app/models/shared.enums';
import { NameData } from '@app/models/shared.models';

export interface NameFormData {
  connectionTypeCode: PersonRoleType | null;
  lastName: string | null;
  middleName: string | null;
  firstName: string | null;
}

export interface NameFormGroup {
  connectionTypeCode: FormControl<PersonRoleType | null>;
  lastName: FormControl<string | null>;
  middleName: FormControl<string | null>;
  firstName: FormControl<string | null>;
}

function requiredValidator(required: boolean) {
  return required ? [Validators.required] : [];
}

export function getNameFormGroup(
  fb: FormBuilder,
  required: boolean = false,
  personRoleCode: PersonRoleType | null = null,
): FormGroup<NameFormGroup> {
  return fb.nonNullable.group({
    connectionTypeCode: fb.control<PersonRoleType | null>(personRoleCode),
    lastName: fb.control<string | null>(null, requiredValidator(required)),
    middleName: fb.control<string | null>(null),
    firstName: fb.control<string | null>(null, requiredValidator(required)),
  });
}

export function getNameFormGroupFromPersonRequest(
  fb: FormBuilder,
  required: boolean = false,
  person: PersonRequest,
  connectionTypeCode: PersonRoleType,
): FormGroup<NameFormGroup> {
  return fb.nonNullable.group({
    connectionTypeCode: fb.nonNullable.control<PersonRoleType | null>(
      connectionTypeCode,
    ),
    lastName: fb.control<string>(person.lastName!, requiredValidator(required)),
    middleName: fb.control<string | null>(person.midName),
    firstName: fb.control<string>(
      person.firstName!,
      requiredValidator(required),
    ),
  });
}

export function getNameFormGroupFromPersonSimpleWithRole(
  fb: FormBuilder,
  required: boolean = false,
  person: PersonSimpleWithRole,
): FormGroup<NameFormGroup> {
  return fb.nonNullable.group({
    connectionTypeCode: fb.nonNullable.control<PersonRoleType | null>(
      person.role.code as PersonRoleType,
    ),
    lastName: fb.control<string>(person.lastName!, requiredValidator(required)),
    middleName: fb.control<string | null>(person.midName),
    firstName: fb.control<string>(
      person.firstName!,
      requiredValidator(required),
    ),
  });
}

export function getTitleNameData(
  formGroup: FormGroup<NameFormGroup>,
): NameData {
  return {
    connectionTypeCode: formGroup.controls.connectionTypeCode.value,
    lastName: formGroup.controls.lastName.value!,
    middleName:
      formGroup.controls.middleName === null
        ? null
        : formGroup.controls.middleName.value,
    firstName: formGroup.controls.firstName.value!,
  };
}

export function patchNameFormFromPersonSimpleWithRole(
  formGroup: FormGroup<NameFormGroup>,
  patchData: PersonSimpleWithRole,
): void {
  formGroup.controls.lastName.setValue(patchData.lastName!);
  formGroup.controls.middleName.setValue(patchData.midName);
  formGroup.controls.firstName.setValue(patchData.firstName!);
  formGroup.controls.connectionTypeCode.setValue(
    patchData.role.code as PersonRoleType,
  );
}

export function getNameFormValueFromPersonSimpleWithRole(
  person: PersonSimpleWithRole | null,
): NameFormData {
  return {
    connectionTypeCode:
      person === null ? null : (person.connection.id as PersonRoleType),
    lastName: person === null ? null : person.lastName,
    middleName: person === null ? null : person.midName,
    firstName: person === null ? null : person.firstName,
  };
}
